import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Table, TableHead,
  TableBody, TableRow, TableCell, Checkbox, Select, MenuItem,
  FormControl, CircularProgress, TableContainer, Paper,
} from '@material-ui/core';
import { Warning } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';

// ─── Styles (mirrors SelectBatchesDialog) ────────────────────────────────────

const useStyles = makeStyles(() => ({
  tableWrap: {
    border: '1px solid #eaeaea',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: '#f4f4f4',
    fontWeight: 700,
    fontSize: '0.75rem',
    color: '#333',
    padding: '9px 12px',
    borderBottom: '2px solid #eaeaea',
    whiteSpace: 'nowrap',
  },
  tableCell: {
    fontSize: '0.8rem',
    color: '#333',
    padding: '8px 12px',
    borderBottom: '1px solid #eaeaea',
  },
  emptyRow: {
    textAlign: 'center',
    padding: 24,
    color: '#999',
    fontSize: '0.82rem',
    fontStyle: 'italic',
  },
  submitBtn: {
    backgroundColor: '#ec6535',
    color: '#fff',
    textTransform: 'none',
    borderRadius: '2rem',
    padding: '8px 28px',
    fontWeight: 600,
    fontSize: '0.85rem',
    '&:hover': { backgroundColor: '#c84e20' },
    '&:disabled': { backgroundColor: '#f5b99d', color: '#fff' },
  },
  // Black outline + black dropdown arrow for both Select fields
  blackSelect: {
    fontSize: '0.82rem',
    color: '#000',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000 !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000 !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#000 !important',
    },
    '& .MuiSelect-icon': {
      color: '#000',
    },
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build the API request object from the triggering row.
 * sponsorShipmentID is 0 in the pre-schedule view (no ID yet),
 * or the actual ID in the post-shipment view.
 */
function buildRequest(row) {
  return {
    aslNumber:               row.aslNumber              || row.scheduleAgreementNumber || '',
    temperature:             row.tempRangeAndSetPoint   || '',
    deliveryAddresseeOrgName: row.deliveryAddresseeOrgName || '',
    sponsorShipmentID:       row.sponsorShipmentID && row.sponsorShipmentID !== '-'
                               ? row.sponsorShipmentID
                               : 0,
  };
}

/**
 * Simulate the /fetchBatchesForShipmentIDGeneration API call.
 * Replace with real axios.post('/fetchBatchesForShipmentIDGeneration', requestBody)
 */


// ─── Component ────────────────────────────────────────────────────────────────

// Build the initial single-row table entry from the triggered row object
function buildRowBatch(row) {
  if (!row) return [];
  return [{
    vendorBatchNumber:       row.vendorBatchNumber,
    fgMaterialCode:          row.fgMaterialCode   || '',
    fgMaterialName:          row.fgMaterialName   || '',
    brand:                   row.brand             || '',
    noOfPallets:             row.noOfPallets,
    deliveryAddresseeOrgName: row.deliveryAddresseeOrgName || '—',
    temperature:             row.tempRangeAndSetPoint     || '—',
    isTriggered:             true,
  }];
}

const ShipmentScheduleDialog = ({ open, onClose, onProceed, row }) => {
  const classes = useStyles();

  const [temperature, setTemperature]             = useState('');
  const [truckCapacity, setTruckCapacity]         = useState('');
  const [temperatureList, setTemperatureList]     = useState([]);
  const [truckCapacityList, setTruckCapacityList] = useState([]);
  const [batches, setBatches]                     = useState(() => buildRowBatch(row));
  const [checked, setChecked]                     = useState(() => row ? { [row.vendorBatchNumber]: true } : {});
  const [apiLoading, setApiLoading]               = useState(false);
  const [submitLoading, setSubmitLoading]         = useState(false);
console.log("batches",batches);

  // On open: load dropdowns + reset table to triggered row only (no batches API call yet)
  useEffect(() => {
    if (!open) return;

    // Reset to triggered row only — button stays disabled until temperature selected
    setBatches(buildRowBatch(row));
    setChecked(row ? { [row.vendorBatchNumber]: true } : {});
    setTemperature('');
    setTruckCapacity('');

    const base = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;
    const fetchDropdowns = async () => {
      try {
        const [tempRes, truckRes] = await Promise.all([
          axios.get(`${base}/fetchTemperatureDetails/false`),
          axios.get(`${base}/fetchTruckCapacityDetails/false`),
        ]);
        // De-duplicate temperature values by name — the vault can hold repeated
        // rows (e.g. several "Ambient"), which would otherwise appear multiple
        // times in the dropdown. Keep the first occurrence of each name.
        const activeTemps = Array.isArray(tempRes?.data?.temperatureDetails)
          ? tempRes.data.temperatureDetails.filter((t) => t.activated && !t.markForDelete)
          : [];
        const seenTemp = new Set();
        setTemperatureList(
          activeTemps.filter((t) => {
            const key = (t.temperature || '').trim().toLowerCase();
            if (!key || seenTemp.has(key)) return false;
            seenTemp.add(key);
            return true;
          })
        );
//         setTemperatureList([
//     {
//         "id": 1,
//         "temperature": "Ambient",
//         "activated": true,
//         "markForDelete": false,
//         "lastUpdatedBy": "support@collabrix.co",
//         "lastUpdatedDate": "2026-05-25T10:21:19.540Z"
//     },
//     {
//         "id": 2,
//         "temperature": "Dual",
//         "activated": true,
//         "markForDelete": false,
//         "lastUpdatedBy": "support@collabrix.co",
//         "lastUpdatedDate": "2026-05-25T10:21:19.540Z"
//     },
//     {
//         "id": 3,
//         "temperature": "coldstore",
//         "activated": true,
//         "markForDelete": false,
//         "lastUpdatedBy": "support@collabrix.co",
//         "lastUpdatedDate": "2026-05-25T10:21:19.540Z"
//     }
// ])
        // De-duplicate truck-capacity rows and drop any DB "All" row — the dropdown
        // already renders its own "All" option, so a vault "All" would show twice.
        // (Removing the unwanted 16/32 capacities is a data fix in the vault.)
        const activeTrucks = Array.isArray(truckRes?.data?.truckCapacityDetails)
          ? truckRes.data.truckCapacityDetails.filter((t) => t.activated && !t.markForDelete)
          : [];
        const seenTruck = new Set();
        setTruckCapacityList(
          activeTrucks.filter((t) => {
            const label = (t.truckCapacity || '').trim().toLowerCase();
            if (label === 'all') return false;
            const key = `${label}|${t.noOfPallets}`;
            if (seenTruck.has(key)) return false;
            seenTruck.add(key);
            return true;
          })
        );
      } catch (e) {
        setTemperatureList([]);
        setTruckCapacityList([]);
      }
    };
    fetchDropdowns();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ─────────────────────────────────────────────────────────
  const triggeredBatch = row ? row.vendorBatchNumber : null;

  const totalSelectedPallets = batches
    .filter((b) => checked[b.vendorBatchNumber])
    .reduce((sum, b) => sum + (parseInt(b.noOfPallets, 10) || 0), 0);

  const getCapacityLimit = useCallback(() => {
    if (!truckCapacity) return Infinity;
    const entry = truckCapacityList.find((t) => t.truckCapacity === truckCapacity);
    if (!entry) return Infinity;
    return entry.noOfPallets === null ? Infinity : entry.noOfPallets;
  }, [truckCapacity, truckCapacityList]);

  const overCapacity  = !!truckCapacity && totalSelectedPallets > getCapacityLimit();
  // All batch objects whose checkbox is checked (triggered + user-selected)
  const selectedBatches = batches.filter((b) => checked[b.vendorBatchNumber]);
  const hasSelection  = selectedBatches.length > 0;
  // Button enabled as soon as there is a selection and no pallet error — initial grayed-out row qualifies
  const canSubmit     = !!temperature && !!truckCapacity && hasSelection && !overCapacity && !submitLoading;

  const handleCheck = useCallback((vendorBatchNumber) => {
    // Triggered batch cannot be unchecked
    if (vendorBatchNumber === triggeredBatch) return;
    setChecked((prev) => ({ ...prev, [vendorBatchNumber]: !prev[vendorBatchNumber] }));
  }, [triggeredBatch]);

  // Temperature selected → call API with selected temp → load batches (triggered batch stays pre-checked)
  const handleTemperatureChange = (e) => {
    const val = e.target.value;
    setTemperature(val);
    if (!row) return;
    setApiLoading(true);
    axios
      .post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchBatchesForShipmentIDGeneration`,
        {
          aslNumber: row.aslNumber,
          temperature: val,
          deliveryAddresseeOrgName: row.deliveryAddresseeOrgName,
          sponsorShipmentID: 0,
        }
      )
      .then((res) => {
        if (res.data.status) {
          const list = Array.isArray(res.data?.data?.batchesList) ? res.data.data.batchesList : [];
          const mapped = list.map((b) => ({
            ...b,
            isTriggered: b.vendorBatchNumber === triggeredBatch,
          }));
          setBatches(mapped);
          // setBatches(res.data?.data?.batchesList)
// setBatches([

//     {
//         "schrockenDeliveryScheduleBatchID": 53419,
//         "freightForwarderScheduleID": null,
//         "deliveryCity": null,
//         "deliveryAddresseeOrgName": null,
//         "scheduleAgreementNumber": "4500006103",
//         "fgMaterialCode": "2388",
//         "noOfPallets": null,
//         "vendorBatchNumber": "PQR6103",
//         "deliveryStreetAddress1": null,
//         "deliveryStreetAddress2": null,
//         "aslNumber": "5431",
//         "expiryDate": "2026-05-27T12:41:09.500",
//         "dispatchQuantity": 1800,
//         "netWeight": null,
//         "grossWeight": null,
//         "fgMaterialName": "CEFF DRY SYRUP (30ML)(SFG)",
//         "deliveryState": null,
//         "deliveryCountry": null,
//         "deliveryPostalCode": null,
//         "producedQuantity": 1500,
//         "temperature": "Ambient",
//         "manufacturingDate": "2026-04-27T12:41:09.500",
//         "batchUOM": "BT",
//         "brand": "Baraclude",
//     },
//        {
//         "schrockenDeliveryScheduleBatchID": 53420,
//         "freightForwarderScheduleID": null,
//         "deliveryCity": null,
//         "deliveryAddresseeOrgName": null,
//         "scheduleAgreementNumber": "4500006103",
//         "fgMaterialCode": "2388",
//         "noOfPallets": null,
//         "vendorBatchNumber": "PQR6105",
//         "deliveryStreetAddress1": null,
//         "deliveryStreetAddress2": null,
//         "aslNumber": "5433",
//         "expiryDate": "2026-05-27T12:41:09.500",
//         "dispatchQuantity": 1800,
//         "netWeight": null,
//         "grossWeight": null,
//         "fgMaterialName": "CEFF DRY SYRUP (30ML)(SFG)",
//         "deliveryState": null,
//         "deliveryCountry": null,
//         "deliveryPostalCode": null,
//         "producedQuantity": 1500,
//         "temperature": "Ambient",
//         "manufacturingDate": "2026-04-27T12:41:09.500",
//         "batchUOM": "BT",
//         "brand": "Baraclude",
//     },
//        {
//         "schrockenDeliveryScheduleBatchID": 53421,
//         "freightForwarderScheduleID": null,
//         "deliveryCity": null,
//         "deliveryAddresseeOrgName": null,
//         "scheduleAgreementNumber": "4500006103",
//         "fgMaterialCode": "2388",
//         "noOfPallets": null,
//         "vendorBatchNumber": "PQR6104",
//         "deliveryStreetAddress1": null,
//         "deliveryStreetAddress2": null,
//         "aslNumber": "5433",
//         "expiryDate": "2026-05-27T12:41:09.500",
//         "dispatchQuantity": 1800,
//         "netWeight": null,
//         "grossWeight": null,
//         "fgMaterialName": "CEFF DRY SYRUP (30ML)(SFG)",
//         "deliveryState": null,
//         "deliveryCountry": null,
//         "deliveryPostalCode": null,
//         "producedQuantity": 1500,
//         "temperature": "Ambient",
//         "manufacturingDate": "2026-04-27T12:41:09.500",
//         "batchUOM": "BT",
//         "brand": "Baraclude",
//     }


// ])
          // Keep triggered batch checked, reset others
          const newChecked = { [triggeredBatch]: true };
          list.forEach((b) => {
            if (b.vendorBatchNumber !== triggeredBatch) newChecked[b.vendorBatchNumber] = false;
          });
          setChecked(newChecked);
        }
      })
      .catch(() => {})
      .finally(() => setApiLoading(false));
  };

  const handleSubmit = () => {
    setSubmitLoading(true);
    setTimeout(() => {
      setSubmitLoading(false);
      if (onProceed) onProceed(selectedBatches, { temperature, truckCapacity });
    }, 500);
  };
console.log("selectedBatches",selectedBatches);
  // ── Display values from row ────────────────────────────────────────────────
  const cmoMfgPlant  = row ? (row.vendorName || row.cmoMfgPlant || '—') : '—';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>

      {/* ── Title ── */}
      <DialogTitle>
        <Typography
          align="center"
          style={{ fontWeight: 'bold', marginTop: '1rem' }}
          variant="h5"
        >
          Select Batches for Shipment Schedule ID Request
        </Typography>
        <Typography
          variant="body2"
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'absolute',
            top: '12px',
            right: '10px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
          onClick={onClose}
        >
          Cancel{" "} <CloseIcon style={{ fontSize: '35px', color: '#FF0000' }} />
        </Typography>
      </DialogTitle>

      <DialogContent style={{ padding: '20px 24px', overflowY: 'visible'}}>

        {/* ── Blue Ribbon ── */}
        <TableContainer
          component={Paper}
          style={{ backgroundColor: '#f2f8fb', marginBottom: '10px', border: '1px solid #ebeced' }}
        >
          <Table>
            <colgroup>
              <col style={{ width: '18%' }} />   {/* CMO - MFG Plant */}
              <col style={{ width: '28%' }} />   {/* Temperature (read-only) */}
              <col style={{ width: '28%' }} />   {/* Truck Capacity */}
              <col />                             {/* spacer */}
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700, borderBottom: 'none', paddingBottom: 2 }}>
                  CMO - MFG Plant
                </TableCell>
                <TableCell style={{ fontWeight: 700, borderBottom: 'none', paddingBottom: 2 }}>
                  Temperature <span style={{ color: 'red' }}>*</span>
                </TableCell>
                <TableCell style={{ fontWeight: 700, borderBottom: 'none', paddingBottom: 2 }}>
                  Truck Capacity <span style={{ color: 'red' }}>*</span>
                </TableCell>
                <TableCell style={{ borderBottom: 'none' }} />
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                {/* CMO - MFG Plant (read-only) */}
                <TableCell style={{ paddingTop: 4 }}>
                  Oxford - Durham
                  {/* {cmoMfgPlant} */}
                </TableCell>

                {/* Temperature dropdown */}
                <TableCell style={{ paddingTop: 4 }}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Select
                      value={temperature}
                      onChange={handleTemperatureChange}
                      displayEmpty
                      className={classes.blackSelect}
                    >
                      <MenuItem value="" disabled>
                        <span style={{ color: '#000', fontSize: '0.82rem' }}>Select temperature</span>
                      </MenuItem>
                      {temperatureList.map((t) => (
                        <MenuItem key={t.id} value={t.temperature} style={{ fontSize: '0.82rem' }}>
                          {t.temperature}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                {/* Truck Capacity dropdown */}
                <TableCell style={{ paddingTop: 4 }}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Select
                      value={truckCapacity}
                      onChange={(e) => setTruckCapacity(e.target.value)}
                      displayEmpty
                      className={classes.blackSelect}
                    >
                      <MenuItem value="" disabled style={{ fontSize: '0.82rem' }}>
                        Select Capacity
                      </MenuItem>
                      <MenuItem value="all" style={{ fontSize: '0.82rem' }}>
                        All
                      </MenuItem>
                      {truckCapacityList.map((t) => (
                        <MenuItem key={t.id} value={t.truckCapacity} style={{ fontSize: '0.82rem' }}>
                          {t.truckCapacity} ({t.noOfPallets} pallets)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell /> {/* spacer */}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Batches Table ── */}
        <Box className={classes.tableWrap} style={{ maxHeight: 300, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  className={classes.tableHeader}
                  padding="checkbox"
                  align="center"
                  style={{ width: 44 }}
                >
                  Selection
                </TableCell>
                <TableCell className={classes.tableHeader}>Batch Number</TableCell>
                <TableCell className={classes.tableHeader}>Product Code / Description</TableCell>
                <TableCell className={classes.tableHeader}>Brand</TableCell>
                <TableCell className={classes.tableHeader}>No. of Pallets</TableCell>
                <TableCell className={classes.tableHeader}>Destination</TableCell>
                <TableCell className={classes.tableHeader}>Temperature</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Loading state */}
              {apiLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 32 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}

              {/* Empty state */}
              {!apiLoading && batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyRow}>
                    No batches available for this shipment.
                  </TableCell>
                </TableRow>
              )}

              {/* Batch rows */}
              {!apiLoading && batches.map((batch) => {
                const isTriggered = batch.isTriggered || batch.vendorBatchNumber === triggeredBatch;
                const rowStyle = isTriggered ? { backgroundColor: '#f8f9ff' } : {};
                const cellStyle = isTriggered ? { color: '#b0b0b0' } : {};
                return (
                  <TableRow key={batch.vendorBatchNumber} hover={!isTriggered} style={rowStyle}>
                    <TableCell padding="checkbox" align="center">
                      <Checkbox
                        checked={isTriggered ? true : Boolean(checked[batch.vendorBatchNumber])}
                        onChange={() => handleCheck(batch.vendorBatchNumber)}
                        color="primary"
                        disabled={isTriggered}
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell} style={cellStyle}>
                      {batch.vendorBatchNumber}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={cellStyle}>
                      {batch.fgMaterialCode} <br /> {batch.fgMaterialName}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={cellStyle}>{batch.brand}</TableCell>
                    <TableCell className={classes.tableCell} style={cellStyle}>
                      {batch.noOfPallets ?? '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={cellStyle}>
                      {batch.deliveryAddresseeOrgName || '—'}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={cellStyle}>{batch.temperature}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* ── Over-capacity warning ── */}
        {overCapacity && (
          <Box display="flex" alignItems="center" style={{ gap: 8, color: '#c62828', marginTop: 12 }}>
            <Warning fontSize="small" />
            <Typography style={{ fontSize: '0.82rem', color: '#c62828' }}>
              Total of the column No. of Pallets is more than the selected Truck Capacity
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* ── Submit ── */}
      <DialogActions style={{ justifyContent: 'center', padding: '16px 24px' }}>
        <Button
          className={classes.submitBtn}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {submitLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Provide Information To Logistics For Scheduling'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentScheduleDialog;
